import { SxProps, Theme } from '@mui/material/styles';

// Module color definitions
export const moduleColors = {
  configuration: {
    primary: '#1976D2',
    light: '#42A5F5',
    hover: '#1565C0',
    background: 'rgba(25, 118, 210, 0.04)',
    border: 'rgba(25, 118, 210, 0.2)',
  },
  administration: {
    primary: '#F44336',
    light: '#EF5350',
    hover: '#E53935',
    background: 'rgba(244, 67, 54, 0.04)',
    border: 'rgba(244, 67, 54, 0.2)',
  },
  stock: {
    primary: '#2196F3',
    light: '#42A5F5',
    hover: '#1E88E5',
    background: 'rgba(33, 150, 243, 0.04)',
    border: 'rgba(33, 150, 243, 0.2)',
  },
  commercial: {
    primary: '#4CAF50',
    light: '#81C784',
    hover: '#388E3C',
    background: 'rgba(76, 175, 80, 0.04)',
    border: 'rgba(76, 175, 80, 0.2)',
  },
  financier: {
    primary: '#9C27B0',
    light: '#BA68C8',
    hover: '#7B1FA2',
    background: 'rgba(156, 39, 176, 0.04)',
    border: 'rgba(156, 39, 176, 0.2)',
  },
  production: {
    primary: '#FF5722',
    light: '#FF8A65',
    hover: '#E64A19',
    background: 'rgba(255, 87, 34, 0.04)',
    border: 'rgba(255, 87, 34, 0.2)',
  },
  hr: {
    primary: '#00897B',
    light: '#4DB6AC',
    hover: '#00695C',
    background: 'rgba(0, 137, 123, 0.04)',
    border: 'rgba(0, 137, 123, 0.2)',
  },
  comptoir: {
    primary: '#3949AB',
    light: '#7986CB',
    hover: '#283593',
    background: 'rgba(57, 73, 171, 0.04)',
    border: 'rgba(57, 73, 171, 0.2)',
  },
  default: {
    primary: '#64748b',
    light: '#94a3b8',
    hover: '#475569',
    background: '#f1f5f9',
    border: '#d1d5db',
  },
};

export type ModuleType = 'configuration' | 'administration' | 'stock' | 'commercial' | 'financier' | 'production' | 'hr' | 'comptoir' | 'default';

// Function to get module-specific table styles
export const getModuleTableStyles = (module: ModuleType = 'default'): SxProps<Theme> => {
  const colors = moduleColors[module];

  return (theme: Theme) => ({
    '& .MuiTableContainer-root': {
      borderRadius: '16px',
      boxShadow: theme.palette.mode === 'light'
        ? '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)'
        : '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)',
      border: `1px solid ${colors.border}`,
      overflow: 'hidden',
      backgroundColor: theme.palette.background.paper,
      padding: 0,
      margin: 0,
      height: 'fit-content',
      width: '100%',
      maxWidth: 'none',
    },
    '& .MuiTable-root': {
      borderCollapse: 'separate',
      borderSpacing: 0,
      backgroundColor: 'transparent',
      padding: 0,
      margin: 0,
      height: 'fit-content',
      '& .MuiTableHead-root': {
        '& .MuiTableRow-root': {
          backgroundColor: colors.background,
          '& .MuiTableCell-root': {
            backgroundColor: 'transparent',
            borderBottom: `2px solid ${colors.border}`,
            borderRight: 'none',
            padding: '8px 20px',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: colors.primary,
            textTransform: 'none',
            letterSpacing: '0.01em',
            position: 'relative',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
            '&:last-child': {
              paddingRight: '24px',
            },
          },
        },
      },
      '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
          backgroundColor: theme.palette.background.paper,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          borderBottom: 'none',
          '&:hover': {
            backgroundColor: colors.background,
            '& .MuiTableCell-root': {
              color: theme.palette.text.primary,
            },
          },
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${colors.border}`,
            borderRight: 'none',
            padding: '8px 20px',
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
            fontWeight: 400,
            lineHeight: 1.5,
            position: 'relative',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
            '&:last-child': {
              paddingRight: '24px',
            },
          },
          '&:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },
  });
};

// Ultra-modern minimalistic table theme with clean design and subtle elegance
export const modernTableStyles: SxProps<Theme> = getModuleTableStyles('default');

// Refined cell styles with optimal spacing and typography
export const modernCellStyles: SxProps<Theme> = (theme: Theme) => ({
  padding: '8px 20px !important',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  fontWeight: 400,
  '&.MuiTableCell-head': {
    padding: '8px 20px !important',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: theme.palette.custom.subtleText,
  },
  '&:first-of-type': {
    paddingLeft: '24px !important',
  },
  '&:last-child': {
    paddingRight: '24px !important',
  },
});

// Function to get module-specific pagination styles
export const getModulePaginationStyles = (module: ModuleType = 'default'): SxProps<Theme> => {
  const colors = moduleColors[module];

  return (theme: Theme) => ({
    '& .MuiTablePagination-root': {
      borderTop: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      '& .MuiTablePagination-toolbar': {
        padding: '16px 24px',
        minHeight: '64px',
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: '0.8125rem',
        color: colors.primary,
        fontWeight: 400,
        margin: 0,
      },
      '& .MuiTablePagination-select': {
        fontSize: '0.8125rem',
        borderRadius: '8px',
        padding: '6px 12px',
        border: `1px solid ${colors.border}`,
        backgroundColor: theme.palette.background.paper,
        marginLeft: '8px',
        marginRight: '16px',
        '&:hover': {
          backgroundColor: colors.background,
          borderColor: colors.primary,
        },
        '&:focus': {
          borderColor: colors.primary,
          boxShadow: `0 0 0 3px ${colors.background}`,
        },
      },
      '& .MuiTablePagination-actions': {
        marginLeft: '16px',
        '& .MuiIconButton-root': {
          padding: '8px',
          borderRadius: '8px',
          color: colors.primary,
          border: '1px solid transparent',
          '&:hover': {
            backgroundColor: colors.background,
            color: colors.hover,
            border: `1px solid ${colors.border}`,
          },
          '&.Mui-disabled': {
            color: theme.palette.custom.disabledText,
            border: '1px solid transparent',
          },
        },
      },
    },
  });
};

// Elegant pagination with minimal design
export const modernPaginationStyles: SxProps<Theme> = getModulePaginationStyles('default');

// Function to get complete module-specific table theme
export const getModuleTableTheme = (module: ModuleType = 'default'): SxProps<Theme> => {
  return (theme: Theme) => {
    const tableStylesFn = getModuleTableStyles(module) as (theme: Theme) => Record<string, any>;
    const paginationStylesFn = getModulePaginationStyles(module) as (theme: Theme) => Record<string, any>;

    return {
      ...tableStylesFn(theme),
      ...paginationStylesFn(theme),
    };
  };
};

// Combined modern table theme
export const modernTableTheme: SxProps<Theme> = getModuleTableTheme('default');

// Keep backward compatibility
export const excelTableTheme = modernTableTheme;

export default excelTableTheme;
